/**
 * Точка входа оверлея — того, что грузится в Browser Source.
 *
 * Здесь только сборка модулей воедино: настройки из адреса,
 * транспорт, состояние сцены, отрисовка. Вся логика, которую есть
 * смысл проверять, живёт в hub.ts, render.ts, geometry.ts и setup.ts,
 * а состояние сцены — в общем с панелью @owm/scene.
 */

import { Transport } from '@owm/transport';

import { startAudio } from './audio.js';
import { activeFromEvent, askObsScene, geometryMessage, readGeometry } from './geometry.js';
import { readSettings, socketURL } from './hub.js';
import { answerProbe, probeIdOf } from './probe.js';
import { Renderer } from './render.js';
import { createSetupFrame, reloadCommand, setupCommand } from './setup.js';
import {
  applyMessage,
  decodeMessage,
  INITIAL_SCENE,
  decodeAirFrame,
  decodeCameraFrame,
  parseAir,
  parseAudio,
  parseEmbeds,
  parseLayerCommand,
  parseLease,
  parseCameras,
  parseLive,
  LIVE_TTL_MS,
  parseSketch,
  SKETCH_TTL_MS,
  parseStop,
  parsePlayback,
  parseServerTime,
  parseSoundPlay,
  parseSoundStop,
  parseSounds,
  type SceneState,
} from '@owm/scene';
import { clock, watchFonts } from '@owm/widgets';

import { Warmup } from './warmup.js';

const stage = document.getElementById('stage');
if (!stage) throw new Error('в разметке нет элемента #stage');

const settings = readSettings(location.href);

let state: SceneState = INITIAL_SCENE;
let obsScene: { width: number; height: number } | null = null;

// Что OBS сказал о показе источника. null — не говорил вовсе:
// обычный браузер или версия постарше
let obsActive: boolean | null = null;

// Состояние соединения выносится в атрибут корня: в OBS нет
// DevTools под рукой, и единственный способ понять, что оверлей
// подключён, — посмотреть на страницу
function markConnection(value: string): void {
  document.documentElement.dataset.connection = value;
}

// Поломка тоже выносится на экран, и по той же причине: пустой
// оверлей неотличим от «ещё не подключился», а стример должен
// понимать, что происходит
function markProblem(problem: string): void {
  if (problem === '') {
    delete document.documentElement.dataset.problem;
    return;
  }
  document.documentElement.dataset.problem = problem;
}

if (!settings.token) {
  // Оверлей без токена — это открытый по ошибке адрес. Молчать
  // здесь нельзя: стример увидит пустой экран и не поймёт, почему
  markConnection('unconfigured');
  stage.textContent = 'Оверлей не настроен: в адресе нет токена';
} else {
  // Звук поднимается ДО отрисовки: слой со звуком, созданный раньше
  // графа, замолчал бы навсегда — маршрут в граф берётся один раз,
  // при создании слоя
  const audio = startAudio();

  // Ожидание шрифтов начинается ДО отрисовки: текстовый слой
  // спрашивает готовность на каждом применении свойств, и не начатое
  // ожидание означало бы, что своё семейство не появится никогда
  watchFonts();

  // Прогрев объявляет свой кеш виджетам сразу: картинку
  // припаркованного слоя прогревает сам виджет, и без объявленного
  // кеша она ушла бы мимо бюджета
  const warmup = new Warmup(audio.context);

  /*
     СТРАХОВКА ОТ ЗАЛИПШЕГО ТРАНЗИЕНТА (см. LIVE_TTL_MS).

     Срок берётся общий, из `@owm/scene`: под него настроен и повтор
     на стороне панели. Своё число здесь означало бы, что оверлей
     забывает движение раньше, чем держащий мышь успевает о себе
     напомнить, — и слой пропадал бы из кадра, стоило человеку
     замереть.
  */
  let liveTimer: ReturnType<typeof setTimeout> | null = null;

  function forgetLiveLater(): void {
    if (liveTimer !== null) clearTimeout(liveTimer);

    liveTimer = setTimeout(() => {
      liveTimer = null;
      renderer.clearLive();
      renderer.render(state);
    }, LIVE_TTL_MS);
  }

  /*
    Срок жизни рисунка в работе (см. SKETCH_TTL_MS).

    Берётся общий, из `@owm/scene`: под него настроен повтор
    на стороне панели. Своё число здесь означало бы, что оверлей
    забывает штрих раньше, чем рисующий успевает о себе напомнить, —
    и незаконченная линия пропадала бы, стоило руке замереть.
  */
  let sketchTimer: ReturnType<typeof setTimeout> | null = null;

  function forgetSketchLater(): void {
    if (sketchTimer !== null) clearTimeout(sketchTimer);

    sketchTimer = setTimeout(() => {
      sketchTimer = null;
      renderer.clearSketches();
      renderer.render(state);
    }, SKETCH_TTL_MS);
  }

  const renderer = new Renderer(
    stage,
    (layerID, error) => {
      // Слой, который не удалось нарисовать, не должен останавливать
      // остальную сцену — но и молчать о нём нельзя
      markProblem(`слой ${layerID}: ${error instanceof Error ? error.message : String(error)}`);
    },
    (layerID, state) => {
      // Состояние эмбеда уходит ПАНЕЛИ, а не на экран: надпись
      // об ошибке поверх кадра увидели бы зрители. В панели её
      // видит человек, который может нажать «перезагрузить»
      transport.send(JSON.stringify({ t: 'embedState', layerId: layerID, state }));
    },
  );

  renderer.resize({ width: window.innerWidth, height: window.innerHeight });

  // Рамка настройки живёт в корне страницы, а не внутри сцены:
  // она показывает границы ИСТОЧНИКА, и масштабироваться вместе
  // со сценой ей нечего
  const setup = createSetupFrame(stage);

  function setupSizes(): Parameters<typeof setup.show>[0] {
    return {
      source: { width: window.innerWidth, height: window.innerHeight },
      canvas: { width: state.scene?.canvasW ?? 0, height: state.scene?.canvasH ?? 0 },
    };
  }

  // Размер сцены OBS спрашивается один раз при запуске: он меняется
  // редко, а опрос на каждом кадре стоил бы дороже пользы
  askObsScene(window, (size) => {
    obsScene = size;
  });

  const transport = new Transport(
    { url: socketURL(settings.hub, settings.token) },
    {
      onStateChange: (connection) => markConnection(connection),

      onOpen: () => {
        // Канал берётся из токена на стороне hub, поэтому оверлею
        // нечего сообщать о себе — но представиться он обязан,
        // иначе соединение закроется через пять секунд.
        //
        // Версия отправляется вместе с рукопожатием: сервер по ней
        // решает, слать снапшот или догнать дельтами
        transport.send(
          JSON.stringify({
            t: 'hello',
            clientKind: 'overlay',
            lastVersion: state.version.toString(),
          }),
        );

        reportGeometry();
      },

      onMessage: (data) => {
        // AudioContext стартует спящим, а в Browser Source нет
        // ни мыши, ни клавиатуры — ждать жеста бессмысленно.
        // Будим сами при первом же признаке жизни (docs/04)
        audio.resume();

        // КАДР ЭФИРА — ПЕРВЫМ И БЕЗ РАЗБОРА JSON. Он приходит
        // двоичным сообщением полсотни раз в секунду: больше, чем
        // всё остальное вместе взятое. Прогонять его через цепочку
        // разборщиков, каждый из которых начинается с проверки
        // «а строка ли это», значит платить за звук на каждом шаге
        if (data instanceof ArrayBuffer) {
          /*
            ДВОИЧНЫЙ КАДР — ЗВУК ИЛИ КАМЕРА, и различает их первый
            байт. Версия в начале кадра заведена была ради
            совместимости — незнакомое молча выбрасывается, — она же
            служит здесь развилкой.

            Камера проверяется первой не по важности, а по цене
            ошибки: её разбор отвергает кадр звука по версии,
            и наоборот, так что порядок безопасен в обе стороны.
          */
          const picture = decodeCameraFrame(data);
          if (picture !== null) {
            renderer.cameraFrame(picture);

            return;
          }

          const frame = decodeAirFrame(data);
          if (frame !== null) {
            audio.airFrame(frame);
          }

          return;
        }

        /*
          СТРОКА РАЗБИРАЕТСЯ ОДИН РАЗ, дальше по цепочке идёт объект.

          Оверлей не знает заранее, что ему прислали, и ведёт сообщение
          по разборщикам, пока кто-нибудь его не узнает. Пока разбор
          жил внутри каждого, одна и та же строка разбиралась столько
          раз, сколько разборщиков стояло перед нужным: `live` доходил
          до своего тринадцатым. При перетаскивании тройки слоёв это
          под две с половиной тысячи разборов JSON в секунду — на той
          самой машине, где в этот же момент OBS кодирует видео.

          Не разобралось — значит и узнавать нечего: раньше такое
          сообщение молча доезжало до конца цепочки, где его тоже
          никто не брал.
        */
        const message = decodeMessage(data);
        if (message === null) return;

        // Поправка к часам — ДО всего остального: таймер на сцене
        // задан меткой серверного времени и считает от неё сам,
        // не тикая по сети (docs/04). Разбирается не «вместо»,
        // а «вдобавок»: и welcome, и pong несут ещё и свой смысл,
        // поэтому дальше сообщение идёт как ни в чём не бывало
        const stamp = parseServerTime(message);
        if (stamp !== null) {
          clock().sync(stamp.serverMs, stamp.roundTripMs ?? 0);
        }

        // Проба задержки (задача 1.11). Обрабатывается раньше сцены:
        // отвечать надо в том же кадре, а не после сравнения состояний
        const probeId = probeIdOf(message);
        if (probeId !== null) {
          answerProbe(probeId, {
            now: () => performance.now(),
            // Двойной кадр не случайность. Первый requestAnimationFrame
            // срабатывает ПЕРЕД отрисовкой, и замер по нему занизил бы
            // результат. Вложенный вызов происходит уже после того,
            // как кадр отдан композитору.
            afterFrame: (fn) => requestAnimationFrame(() => requestAnimationFrame(fn)),
            send: (answer) => transport.send(JSON.stringify(answer)),
          });
          return;
        }

        // Режим настройки: рамка по границам источника. Разбирается
        // до сцены — это не изменение сцены, а команда самому оверлею
        const wantSetup = setupCommand(message);
        if (wantSetup !== null) {
          if (wantSetup) setup.show(setupSizes());
          else setup.hide();
          return;
        }

        // Стоп-кран: не изменение сцены, а то, что владелец перекрыл
        // прямо сейчас. Разбирается до сцены и до громкости —
        // он перевешивает и то, и другое
        const stopState = parseStop(message);
        if (stopState !== null) {
          audio.silence(stopState.silence);
          renderer.setStop(stopState);
          // Перерисовываем немедленно: «заглушить всё» выгружает
          // эмбеды из DOM, и ждать следующей операции значило бы
          // оставить чужую страницу звучать до неё
          renderer.render(state);
          return;
        }

        // Громкость канала: тоже не изменение сцены, а отдельная
        // настройка, живущая дольше снапшота
        const audioLevels = parseAudio(message);
        if (audioLevels !== null) {
          audio.apply(audioLevels);
          return;
        }

        // Звук саундборда: не изменение сцены, а событие. Оно ничего
        // не рисует и ничего не оставляет после себя — заводить ради
        // него поле в состоянии значило бы завести то, что надо
        // не забыть сбросить (docs/04)
        const sound = parseSoundPlay(message);
        if (sound !== null) {
          audio.play(sound);
          return;
        }

        // Просьба замолчать. Не стоп-кран: тот глушит ВЕСЬ звук
        // эфира, включая музыку и ролики, и снимается отдельным
        // действием. Здесь кончается одно нажатие
        const silence = parseSoundStop(message);
        if (silence !== null) {
          audio.stopSound(silence.triggerId);
          return;
        }

        // Список звуков для прогрева: не изменение сцены, а то,
        // что оверлею надо скачать заранее. Звук саундборда не слой,
        // и переключение сцены его не касается
        const warmSounds = parseSounds(message);
        if (warmSounds !== null) {
          void warmup.warmSounds(warmSounds);
          return;
        }

        // Эфир своим звуком: настройки декодера приезжают отдельно
        // от кадров и ДО них. Оверлей, открытый посреди эфира,
        // получает их при подключении — иначе разбирать кадры
        // ему нечем
        /*
          Кто вещает камерой. Разбирается до сцены: это не её
          изменение, а состояние комнаты — и без него слой камеры
          показать нечем, декодер настраивают этим списком.
        */
        const cameras = parseCameras(message);
        if (cameras !== null) {
          renderer.setCameras(cameras);

          return;
        }

        const air = parseAir(message);
        if (air !== null) {
          if (air.on && air.config !== undefined) {
            audio.airOn(air.config);
          } else {
            audio.airOff();
          }

          return;
        }

        // Источники эмбедов: адреса в сцене нет вовсе, слой знает
        // только идентификатор. Разбирается до сцены — это не её
        // изменение, а отдельный список, живущий дольше снапшота
        const embeds = parseEmbeds(message);
        if (embeds !== null) {
          renderer.setEmbeds(embeds);
          // Сцена могла прийти раньше источников: перерисовываем,
          // чтобы уже стоящие эмбеды зарядились адресом, а не
          // остались заглушками до следующей правки
          renderer.render(state);
          return;
        }

        /*
          ТРАНЗИЕНТНОЕ ДВИЖЕНИЕ: слой тянут прямо сейчас.

          Разбирается до сцены и в состояние НЕ ПОПАДАЕТ: версия
          от него не растёт. Положи мы это в состояние — оверлей
          считал бы себя ушедшим вперёд, и следующая настоящая дельта
          пришла бы с «неожиданной» версией, вызывая resync на каждое
          движение мыши.

          Без этого зритель видел телепортацию: слой стоял на месте
          весь жест и прыгал в конечную точку по отпусканию кнопки.

          РИСУЕТСЯ НА БЛИЖАЙШЕМ КАДРЕ. Сообщений приходит до
          шестидесяти в секунду на каждый удерживаемый слой, и они
          идут подряд, за доли миллисекунды: экран покажет последнее,
          а промежуточные обошлись бы в полную пересборку сцены
          каждое (см. Renderer.renderLater).
        */
        /*
          СЛОЙ ВЗЯЛИ РУКОЙ.

          Не изменение сцены, а сообщение о том, что модератор держит
          слой прямо сейчас. Оверлею это нужно, чтобы приготовить его
          к показу ЗАРАНЕЕ: припаркованный за краем слой не смонтирован
          вовсе, и внесённый рывком он рождается ровно в миг пересечения
          границы — зритель видит, как картинка догоняет курсор.

          Перерисовываем на БЛИЖАЙШЕМ КАДРЕ, а не сию секунду: смысл
          в том, чтобы узел появился до того, как слой доедет до кадра,
          а не вместе с ним, — и кадр экрана этому запасу не помеха.
          Взятие руки приходит вперемешку с движением, и рисовать
          от каждого отдельно значит рисовать одно и то же дважды
          за шестнадцать миллисекунд (см. Renderer.renderLater).
        */
        const lease = parseLease(message);
        if (lease !== null) {
          renderer.setHeld(lease.layerId, lease.held);
          renderer.renderLater(state);
          return;
        }

        const liveMoves = parseLive(message);
        if (liveMoves !== null) {
          // Кадр жеста несёт столько положений, сколько слоёв тянут:
          // выделяют и двигают пачкой
          for (const move of liveMoves) renderer.setLive(move.layerId, move.transform);
          renderer.renderLater(state);
          forgetLiveLater();
          return;
        }

        /*
          Рисунок в работе: сосед ведёт руку, и зритель видит это
          по ходу, а не готовым результатом через секунду.

          Забывается по СРОКУ, а не по сообщению «я закончил»: такое
          сообщение пришло бы раньше настоящей операции — транзиент
          летит напрямую, а операция идёт через запись в базу, —
          и рисунок мигнул бы: пропал и появился.
        */
        const drawing = parseSketch(message);
        if (drawing !== null) {
          renderer.setSketch(drawing);
          renderer.renderLater(state);
          forgetSketchLater();
          return;
        }

        // Просьба пересоздать слой: тоже команда оверлею, а не
        // изменение сцены. Разбирается до неё по той же причине,
        // что и режим настройки
        /*
          Команда воспроизведения: пауза, продолжить, сначала.
          Разбирается до сцены — это не её изменение, а состояние
          проигрывателя в эту минуту, и в снапшот оно не попадает
          намеренно (см. @owm/scene/layercmd).
        */
        const played = parseLayerCommand(message);
        if (played !== null) {
          renderer.command(played.layerId, played.command, played.positionMs);
          return;
        }

        /*
          ГДЕ СЕЙЧАС РОЛИК — отметка от сервера. Разбирается рядом
          с командами, но это НЕ команда: команда говорит «сделай»,
          отметка — «так теперь есть». Оверлей, открытый посреди
          ролика, попадает в ту же точку, что и все остальные.
        */
        const playback = parsePlayback(message);
        if (playback !== null) {
          renderer.playback(playback);
          return;
        }

        const reloadLayer = reloadCommand(message);
        if (reloadLayer !== null) {
          renderer.reload(reloadLayer);
          return;
        }

        const applied = applyMessage(state, message);

        if (applied.resync) {
          // Разрыв версии: применять дельту поверх неизвестного
          // состояния нельзя — попросим всё заново
          transport.send(JSON.stringify({ t: 'resync' }));
        }

        // Ссылка не изменилась — значит и рисовать нечего.
        // На машине стримера лишняя отрисовка стоит кадров
        if (applied.state === state) return;

        state = applied.state;
        markProblem(state.problem);
        // Пришло настоящее положение — транзиенты устарели все разом
        renderer.clearLive();
        renderer.render(state);

        // Кадр мог измениться вместе со сценой — подпись на рамке
        // обязана говорить о том, что есть сейчас
        setup.update(setupSizes());
      },
    },
  );

  function reportGeometry(): void {
    // «Показывается ли сейчас» складывается из двух ответов, и ни
    // одного не хватает по отдельности. Скрытая страница — это точно
    // не эфир, но страница в Browser Source остаётся видимой и когда
    // источник убран из эфирной сцены; про это знает только OBS.
    // Пока он молчит (обычный браузер, старая версия) считаем,
    // что показывается: врать в сторону «в эфире» безопаснее, чем
    // объявить скрытым работающий оверлей
    const visible = document.visibilityState === 'visible';
    const geometry = readGeometry(window, visible && (obsActive ?? true));

    transport.send(geometryMessage({ ...geometry, obsScene }));
  }

  transport.connect();

  // Размер Browser Source меняют мышью прямо во время эфира:
  // пересчитать вписывание надо сразу, а панель должна узнать,
  // что источник теперь другого размера
  addEventListener('resize', () => {
    renderer.resize({ width: window.innerWidth, height: window.innerHeight });
    reportGeometry();

    // Размер источника меняют мышью как раз тогда, когда его
    // настраивают: подпись на рамке обязана называть новый
    setup.update(setupSizes());
  });

  // OBS сообщает, попал ли источник в эфирную сцену. Это главный
  // ответ на вопрос «видит ли это зритель»: страница остаётся
  // видимой и тогда, когда её никто не показывает
  addEventListener('obsSourceActiveChanged', (event) => {
    const active = activeFromEvent(event);
    if (active === null || active === obsActive) return;

    obsActive = active;
    reportGeometry();
  });

  // А это второй, более грубый ответ: страница спрятана целиком.
  // В OBS случается при выключенном источнике с «Shutdown source
  // when not visible», в обычном браузере — при переходе на другую
  // вкладку
  addEventListener('visibilitychange', () => reportGeometry());

  // Browser Source перезагружает страницу при смене сцены; закрыть
  // сокет явно дешевле, чем ждать, пока hub заметит обрыв по ping
  addEventListener('pagehide', () => transport.close());
}
